import type { InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { useState } from "react";
import { formatCurrencyInput, parseCurrencyInput } from "../../../lib/format";
import { cn } from "../../../lib/utils";

interface FormFieldProps {
  label: string;
  htmlFor: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
  className?: string;
}

export function FormField({ label, htmlFor, hint, error, children, className }: FormFieldProps): React.ReactElement {
  return (
    <div className={className}>
      <label htmlFor={htmlFor} className="text-xs font-medium text-muted-foreground mb-1 block">
        {label}
        {hint && <span className="ml-1 font-normal text-muted-foreground/70">({hint})</span>}
      </label>
      {children}
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
}

const inputBase =
  "flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

export function FormInput({ className, ...props }: InputHTMLAttributes<HTMLInputElement>): React.ReactElement {
  return <input className={cn(inputBase, className)} {...props} />;
}

export function FormSelect({
  className,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement>): React.ReactElement {
  return (
    <select className={cn(inputBase, className)} {...props}>
      {children}
    </select>
  );
}

export function FormTextarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>): React.ReactElement {
  return (
    <textarea
      className={cn(
        "flex min-h-[80px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    />
  );
}

export function GridCellInput({
  value,
  onChange,
  w = "w-20",
  placeholder,
  type = "number",
  onKeyDown,
}: {
  value: string | number | null;
  onChange: (v: string) => void;
  w?: string;
  placeholder?: string;
  type?: "number" | "text" | "currency";
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}): React.ReactElement {
  const [draft, setDraft] = useState<string | null>(null);
  const masked = type !== "number";
  const displayValue = masked
    ? (draft ?? (type === "currency" ? formatCurrencyInput(typeof value === "number" ? value : null) : (value ?? "")))
    : (value ?? "");
  return (
    <input
      type={masked ? "text" : "number"}
      inputMode={masked ? "decimal" : undefined}
      data-cell="true"
      value={displayValue}
      onFocus={masked ? () => setDraft(String(value ?? "")) : undefined}
      onBlur={masked ? () => setDraft(null) : undefined}
      onChange={(e) => {
        if (masked) setDraft(e.target.value);
        onChange(e.target.value);
      }}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      step={type === "number" ? "any" : undefined}
      className={`${w} h-7 rounded border border-input bg-white px-1.5 text-xs text-right tabular-nums focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring`}
    />
  );
}

export function CurrencyInput({
  value,
  onChange,
  id,
  className,
}: {
  value: number | null | undefined;
  onChange: (v: number | null) => void;
  id?: string;
  className?: string;
}): React.ReactElement {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <input
      id={id}
      type="text"
      inputMode="decimal"
      value={draft ?? formatCurrencyInput(value ?? null)}
      onFocus={() => setDraft(value == null ? "" : String(value))}
      onBlur={() => setDraft(null)}
      onChange={(e) => {
        setDraft(e.target.value);
        onChange(parseCurrencyInput(e.target.value));
      }}
      className={cn(inputBase, className)}
    />
  );
}

export function FormCheckbox({
  label,
  checked,
  onChange,
  id,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  id: string;
}): React.ReactElement {
  return (
    <label htmlFor={id} className="flex items-center gap-2 text-sm cursor-pointer">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-input"
      />
      {label}
    </label>
  );
}
